import { Box, Divider, Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import biApi from "../../../../../../../../../api/biApi";
import type { DrillDownInfo, MeasureDrillDownInfo } from "../../../../../../../../../api/types/svTypes.ts";
import useDrillDown from "../../../../../hooks/useDrillDown";
import { selectSessionId } from "../../../../../store/selectors/currentReportSelectors";
import { selectMeasures, selectMeasuresStructure } from "../../../../../store/selectors/metaDataSelectors";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { getActiveLayer } from "../../../../../store/utils/getActiveLayer.ts";
import SplitPane from "../../../../common/split-pane/SplitPane";
import MeasureSelector from "../../chartOfAccounts/MeasureSelector";
import { ChartOfAccountsContextProvider } from "../../chartOfAccounts/contexts/ChartOfAccountsContextProvider";
import { Properties } from "../../chartOfAccounts/properties/Properties";
import FormulaContainer from "../../customMeasure/FormulaContainer";
import { useCustomMeasure } from "../../customMeasure/hooks/useCustomMeasure";
import ReadonlyConditions from "../ReadonlyConditions";
import LedgerAccountsWrapper from "./LedgerAccountsWrapper";

interface Props {
  configuration: DrillDownPage;
  info: DrillDownInfo;
}

export default function ChartOfAccountsPart({ configuration, info }: Props) {
  const storedMeasures = useSelector(selectMeasures);
  const measuresStructure = useSelector(selectMeasuresStructure);
  const sessionId = useSelector(selectSessionId);

  const activeLayer = getActiveLayer(configuration);
  const selectedMeasure = useMemo(
    () => storedMeasures.find((m) => m.name === activeLayer.measure?.meta.name),
    [storedMeasures, activeLayer.measure]
  );

  const drillDownState = useDrillDown(info.combined as MeasureDrillDownInfo);
  const customMeasureState = useCustomMeasure({ measure: selectedMeasure });

  const fetchChartOfAccounts = useCallback(
    () => biApi.getChartOfAccountsDrillDown(sessionId, activeLayer.drillDownId || ""),
    [activeLayer.drillDownId, sessionId]
  );
  const [chartOfAccountsResp, error, { isFetching }] = useFetch(fetchChartOfAccounts);

  if (!selectedMeasure) {
    return null;
  }

  if (error) {
    return <DataLoadingFailed title="Failed load Chart Of Accounts"></DataLoadingFailed>;
  }

  if (isFetching) {
    return <InlineLoader />;
  }

  return (
    <Box sx={{ display: "flex", flex: 1, width: "100%", height: "100%", minHeight: 0 }}>
      <ChartOfAccountsContextProvider>
        <SplitPane
          initialPrimarySize="400px"
          minPrimarySize="400px"
          minSecondarySize="400px"
          primary={
            <Box
              sx={{
                p: "1.3rem",
                pl: 3,
                height: "100%",
                minHeight: 0,
                display: "grid",
                gridTemplateRows: "fit-content(50%) minmax(20%, 1fr)",
                rowGap: 3,
                width: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <ReadonlyConditions state={drillDownState} configuration={configuration} info={info.combined} />
              <Box
                sx={{
                  height: "100%",
                  display: "grid",
                  gridTemplateRows: "minmax(auto, 66px) minmax(auto, 90px) 1px minmax(40%, auto)",
                  gap: 1,
                }}
              >
                <MeasureSelector
                  measures={storedMeasures}
                  measuresStructure={measuresStructure}
                  measure={selectedMeasure}
                />
                <FormulaContainer state={customMeasureState} measure={selectedMeasure} editable={false} hideCheck />
                <Divider sx={{ mx: -3 }} />
                <Box sx={{ minHeight: 0, height: "100%" }}>
                  <Box
                    sx={{
                      height: "100%",
                      overflow: "hidden",
                      boxSizing: "border-box",
                    }}
                  >
                    <Stack
                      spacing={1}
                      sx={{
                        height: "auto",
                        maxHeight: "100%",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                    >
                      <Properties state={customMeasureState} />
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Box>
          }
          secondary={
            <LedgerAccountsWrapper
              configuration={configuration}
              info={info}
              chartOfAccounts={chartOfAccountsResp?.items || []}
            />
          }
        />
      </ChartOfAccountsContextProvider>
    </Box>
  );
}
